import { Search, SlidersHorizontal, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchResultCard } from '@/components/news/SearchResultCard';
import { Seo } from '@/components/seo/Seo';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { appConfig } from '@/config/app';
import { getCategorySummaries, searchNews } from '@/services/newsRepository';

export function SearchPage() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('categoria') ?? '');
  const [category, setCategory] = useState(params.get('categoria') ?? '');
  const [date, setDate] = useState('');
  const debouncedQuery = useDebouncedValue(query);
  const categories = getCategorySummaries();

  const results = useMemo(
    () => searchNews({ query: debouncedQuery, category, date }),
    [category, date, debouncedQuery]
  );

  return (
    <section className="safe-px pb-28 pt-6">
      <Seo
        title="Buscar | El Divergente"
        description="Busqueda instantanea de noticias por titulo, categoria, lugar, persona, fecha o texto."
        canonical={`${appConfig.siteUrl}/buscar`}
        keywords={['buscar noticias', 'Puerto San Julián', 'El Divergente']}
      />
      <h1 className="text-3xl font-black">Buscar</h1>
      <label className="mt-5 flex min-h-12 items-center gap-3 rounded bg-divergent-panel px-4">
        <Search size={20} aria-hidden="true" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Titulo, categoria, lugar, persona, fecha"
          className="min-w-0 flex-1 bg-transparent text-white outline-none placeholder:text-white/35"
        />
        {query ? (
          <button type="button" aria-label="Limpiar búsqueda" onClick={() => setQuery('')}>
            <X size={18} aria-hidden="true" />
          </button>
        ) : null}
      </label>
      <div className="mt-3 rounded bg-divergent-panel p-3">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white/72">
          <SlidersHorizontal size={17} aria-hidden="true" />
          Filtros
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-white/45">
            Categoría
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="mt-2 min-h-11 w-full rounded border border-white/10 bg-black px-3 text-sm text-white outline-none focus:border-divergent-yellow"
            >
              <option value="">Todas</option>
              {categories.map((item) => (
                <option key={item.slug} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-white/45">
            Fecha
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="mt-2 min-h-11 w-full rounded border border-white/10 bg-black px-3 text-sm text-white outline-none focus:border-divergent-yellow"
            />
          </label>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        <div className="text-sm text-white/50">{results.length} resultados</div>
        {results.map((result) => (
          <SearchResultCard key={result.article.id} result={result} />
        ))}
        {results.length === 0 ? (
          <div className="rounded bg-divergent-panel p-5 text-center text-white/58">
            No hay resultados para esos filtros.
          </div>
        ) : null}
      </div>
    </section>
  );
}
